import { z } from 'zod';
import { telefonoArSchema } from './telefono.js';
/**
 * Onboarding del paciente · cuatro pasos (P-01 a P-02d).
 *
 * El paso actual lo decide **el servidor**, no el cliente. Es lo que hace que la
 * reanudación funcione sola: el paciente que abandonó en el paso 2 y vuelve tres
 * días después no ve una app en blanco ni empieza de nuevo — ve el paso 2
 * (`escenarios-excepciones.html` §A).
 */
export const pasoOnboardingSchema = z.enum([
    'LINK_ENVIADO',
    'LINK_ABIERTO',
    'IDENTIDAD_CONFIRMADA',
    'CONSENTIMIENTO_FIRMADO',
    'CUIDADOR_DEFINIDO',
    'COMPLETO',
]);
/** Qué pantalla corresponde a cada estado. El orden es el del flujo. */
export const PANTALLA_POR_ESTADO = {
    LINK_ENVIADO: 'identidad',
    LINK_ABIERTO: 'identidad',
    IDENTIDAD_CONFIRMADA: 'consentimiento',
    CONSENTIMIENTO_FIRMADO: 'cuidador',
    CUIDADOR_DEFINIDO: 'permisos',
    COMPLETO: 'listo',
};
/** Número de paso para el stepper de la UI. `listo` ya no muestra stepper. */
export const NUMERO_DE_PASO = {
    identidad: 1,
    consentimiento: 2,
    cuidador: 3,
    permisos: 4,
    listo: 4,
};
export const TOTAL_DE_PASOS = 4;
/**
 * Estado que el cliente necesita para pintar la pantalla correcta.
 *
 * `alias` y `cirugiaAt` vienen pre-cargados por el equipo médico al dar de alta
 * (P-02a): el paciente **confirma**, no completa. Un adulto mayor recién operado
 * escribiendo su fecha de cirugía en un celular es una fuente de errores.
 */
export const estadoOnboardingSchema = z.object({
    paso: pasoOnboardingSchema,
    pantalla: z.enum(['identidad', 'consentimiento', 'cuidador', 'permisos', 'listo']),
    alias: z.string().min(1),
    cirugiaAt: z.string().date().nullable(),
    /** Versión del texto de consentimiento que corresponde firmar. */
    consentimientoVersion: z.string().min(1),
    tieneCuidador: z.boolean(),
    pushHabilitado: z.boolean().nullable(),
});
/** P-02a · confirmación de identidad. */
export const confirmarIdentidadSchema = z.object({
    /** Fecha de la cirugía en formato ISO (YYYY-MM-DD). */
    cirugiaAt: z.string().date(),
});
/**
 * P-02b · aceptación del consentimiento.
 *
 * El cliente manda el **hash del texto que efectivamente mostró**. El servidor
 * lo compara con el del texto que él sirvió: si no coinciden, la persona aceptó
 * algo distinto de lo que hay registrado y la firma se rechaza. Es lo que hace
 * que el registro pruebe algo.
 */
export const aceptarConsentimientoSchema = z.object({
    version: z.string().min(1),
    textoHash: z.string().length(64),
    /** El scroll completo es obligatorio: sin leerlo, no se acepta. */
    leidoCompleto: z.literal(true),
});
/**
 * P-02c · cuidador, opcional.
 *
 * El teléfono es PII y **no se guarda en Postgres**: se usa para emitir la
 * invitación y no se persiste (`pii-handling.md` §2). Mientras WhatsApp real sea
 * de E5, el envío es un mock y lo que queda es el evento.
 */
export const definirCuidadorSchema = z.discriminatedUnion('accion', [
    z.object({
        accion: z.literal('invitar'),
        /** Vínculo declarado, sin nombres: "hija", "esposo", "vecina". */
        relacion: z.string().min(2).max(40),
        /**
         * Sólo para emitir la invitación. No se persiste.
         *
         * Normalizado a E.164 por `telefonoArSchema`: lo que sale de acá es lo que
         * va a recibir WhatsApp en E5, no lo que la persona tipeó.
         */
        telefono: telefonoArSchema,
    }),
    z.object({ accion: z.literal('saltear') }),
]);
/** P-02d · decisión sobre notificaciones. Instalar la PWA no se puede forzar. */
export const decidirPushSchema = z.object({ habilitado: z.boolean() });
//# sourceMappingURL=onboarding.js.map